"use client";

import {
  keepPreviousData,
  onlineManager,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type { ChatSearchTarget } from "@/actions/chat";
import {
  addGroupMembers,
  archiveChatForUser,
  createGroupChat,
  deleteChatForUser,
  deleteMessage as deleteMessageAction,
  demoteGroupAdmin,
  forwardMessages as forwardMessagesAction,
  getMessageReactors,
  getMyActiveLiveLocations,
  getOrCreateHiiveChat,
  getPinnedMessages,
  getPollVoters,
  leaveGroupChat,
  markViewOnceOpened,
  markVoiceAttachmentPlayed,
  muteChatForUser,
  pinChatForUser,
  pinMessage,
  promoteGroupAdmin,
  reactToMessage,
  removeGroupMember,
  renameGroupChat,
  requestChatUploadTarget,
  searchChatMessages,
  searchChatTargets,
  setDisappearingTimer,
  shareToChat as shareToChatAction,
  startDirectChat,
  stopLiveLocation,
  unarchiveChatForUser,
  unpinMessage,
  updateGroupAvatar,
  updateGroupDescription,
  updateMessage as updateMessageAction,
  votePoll,
} from "@/actions/chat";
import type {
  MessageKind,
  MessagePayload,
  MessageShareMedia,
} from "@/db/schema/chat";
import { chatQueryFns } from "@/features/chat/queries/fns";
import { chatKeys } from "@/features/chat/queries/keys";
import { useAppForeground } from "@/hooks/use-app-foreground";
import {
  useRealtimeChannel,
  useRealtimeChannels,
  useRealtimeReconnect,
  useRealtimeStatus,
  useUserRealtime,
} from "@/hooks/use-realtime";
import {
  HIIVE_BOT_AUTHOR_ID,
  HIIVE_BOT_NAME,
  isHiiveBotHandle,
} from "@/lib/assistant-handles";
import { chatListActivityAt } from "@/lib/chat/chat-list-activity";
import {
  heartbeatChatPresence,
  markChatSeen,
  sendMessage,
  setTyping,
} from "@/lib/chat/chat-rpc";
import {
  isLiveLocationPayload,
  type LiveLocationDurationMinutes,
} from "@/lib/chat/live-location";
import {
  DEFAULT_PIN_DURATION_SEC,
  isActivePin,
  pinExpiresAt,
} from "@/lib/chat/pin-policy";
import { resolveChatPresenceState } from "@/lib/chat-presence";
import type { MediaAssetSlim } from "@/lib/media";
import {
  type MusicAttachment,
  musicAttachmentInput,
} from "@/lib/music/attachment";
import { realtimeClient } from "@/lib/realtime/client";
import { chatChannel, presenceChannel } from "@/lib/realtime/events";
import type { ChatUploadSlot, SignedLinkPreview } from "@/validations/chat";

const MESSAGE_POLL_MS_ACTIVE = 1_500;
const MESSAGE_POLL_MS_BACKGROUND = 4_500;
const CHAT_LIST_POLL_MS_ACTIVE = 2_500;
const CHAT_LIST_POLL_MS_BACKGROUND = 7_500;
const UNREAD_POLL_MS_ACTIVE = 4_000;
const UNREAD_POLL_MS_BACKGROUND = 8_000;
const TYPING_POLL_MS_ACTIVE = 1_200;
const TYPING_POLL_MS_BACKGROUND = 3_500;
const PRESENCE_POLL_MS_ACTIVE = 20_000;
const PRESENCE_POLL_MS_BACKGROUND = 45_000;
const PRESENCE_HEARTBEAT_MS = 45_000;
const POLL_JITTER_MS = 250;
// When the realtime socket is live, queries drop to this slow safety-net poll —
// WebSocket events drive the real-time updates. When the socket is down or
// unconfigured, the original fast adaptive cadence applies (no regression
// without WebSockets).
const REALTIME_FALLBACK_MS = 30_000;

const withJitter = (baseMs: number) => {
  if (typeof window === "undefined") return baseMs;

  return Math.max(
    500,
    baseMs +
      Math.floor(Math.random() * (POLL_JITTER_MS * 2 + 1) - POLL_JITTER_MS),
  );
};

const coerceDate = (value: unknown): Date => {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    return new Date(value);
  }
  return new Date(0);
};

const resolveAdaptivePollMs = (
  activeMs: number,
  backgroundMs: number,
  enabled = true,
  realtimeStatus = realtimeClient.currentStatus,
) => {
  if (!enabled) return false;
  // Socket live → events handle freshness; poll slowly as a safety net.
  if (realtimeStatus === "open") {
    return withJitter(REALTIME_FALLBACK_MS);
  }
  if (typeof document === "undefined") return withJitter(activeMs);
  return withJitter(
    document.visibilityState === "visible" ? activeMs : backgroundMs,
  );
};

const invalidateChatCollections = async (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: chatKeys.userChats("all") }),
    queryClient.invalidateQueries({ queryKey: chatKeys.userChats("archived") }),
    queryClient.invalidateQueries({ queryKey: chatKeys.totalUnreadCount() }),
  ]);
};

const throwIfActionError = <T extends { error?: string | null }>(result: T) => {
  if ("error" in result && result.error) {
    throw new Error(result.error);
  }
  return result;
};

export interface MessageAttachment {
  id: number;
  kind: "image" | "video" | "voice";
  asset: MediaAssetSlim;
  durationMs: number | null;
  waveform: number[] | null;
  voicePlayback?: {
    playedByViewer: boolean;
    playedByOthersCount: number;
    lastPlayedAt: Date | null;
  } | null;
}

export interface MessageAttachmentSummary {
  kind: "image" | "video" | "voice" | "mixed";
  count: number;
}

export interface MessageReadState {
  seenByCount: number;
  seenByPreview: Array<{
    userId: string;
    userName: string | null;
    username: string | null;
    userImage: string | null;
  }>;
  lastSeenAt: Date | null;
  label: string;
  /** Sent = single tick, delivered = grey double, seen = accent double. */
  deliveryState: "sent" | "delivered" | "seen";
}

export interface MessagePollOptionState {
  id: number;
  label: string;
  position: number;
  voteCount: number;
  votedByViewer: boolean;
  /** Up to 3 voters for a per-option avatar preview; empty for anonymous polls. */
  voters: { userId: string; name: string | null; image: string | null }[];
}

export interface MessagePollState {
  id: number;
  question: string;
  allowsMultiple: boolean;
  isAnonymous: boolean;
  closesAt: Date | null;
  isClosed: boolean;
  totalVoters: number;
  options: MessagePollOptionState[];
}

export interface Message {
  assistantName?: string | null;
  assistantHandle?: string | null;
  id: number;
  content: string | null;
  type: "user" | "system";
  kind?: MessageKind;
  payload?: MessagePayload | null;
  poll?: MessagePollState | null;
  forwardedFromMessageId?: number | null;
  expiresAt?: Date | null;
  viewOnce?: boolean;
  viewOnceState?: "available" | "opened" | "sent" | null;
  pinnedAt?: Date | null;
  pinnedExpiresAt?: Date | null;
  pinnedByUserId?: string | null;
  authorId: string;
  authorName: string | null;
  authorUsername: string | null;
  authorImage: string | null;
  replyToId: number | null;
  status: "sent" | "edited" | "deleted";
  editedAt: Date | null;
  createdAt: Date;
  updatedAt?: Date | null;
  clientMessageId?: string | null;
  reactions: Array<{ userId: string; reaction: string }>;
  attachments: MessageAttachment[];
  attachmentSummary?: MessageAttachmentSummary | null;
  readState?: MessageReadState | null;
  localStatus?: "sending" | "failed";
}

export interface ChatTypingUser {
  userId: string;
  userName: string | null;
  username: string | null;
  userImage: string | null;
  expiresAt: Date;
  /** The Hiive bot composing a reply: shown with the bot avatar. */
  bot?: boolean;
}

export interface SeenByUser {
  userId: string;
  seenAt: Date | null;
  userName: string | null;
  username: string | null;
  userImage: string | null;
}

export interface GroupMember {
  userId: string;
  role: "member" | "admin";
  leftAt: Date | null;
  isActive: boolean;
  userName: string | null;
  username: string | null;
  userImage: string | null;
  isSelf: boolean;
}

export interface ChatPresence {
  lastActiveAt: Date | null;
  isActiveNow: boolean;
  label: string | null;
  kind: "active" | "recent" | "offline";
  observedAt?: number;
}

export interface ChatParticipantPreview {
  userId: string;
  userName: string | null;
  username: string | null;
  userImage: string | null;
}

export interface ChatAvatarStackItem {
  id: string;
  imageUrl: string | null;
  fallback: string;
}

export interface ChatListReadState {
  seenByPreview?: MessageReadState["seenByPreview"];
  kind: "sent" | "delivered" | "seen" | "seen_group";
  seenByCount: number;
  lastSeenAt: Date | null;
  label: string;
}

export interface ChatListItem {
  id: number;
  type:
    | "hiive"
    | "hiive_tier"
    | "event"
    | "event_occurrence"
    | "direct"
    | "assistant"
    | "group"
    | "activity_session";
  name: string;
  entityId: number | null;
  hiiveName: string | null;
  hiiveSlug: string | null;
  image: string | null;
  subtitle: string | null;
  participantCount: number;
  participantPreview: ChatParticipantPreview[];
  avatarStack: ChatAvatarStackItem[];
  presence: ChatPresence | null;
  directChatParticipant?: {
    userId: string;
    username: string | null;
    userName: string | null;
    userImage: string | null;
  } | null;
  directChatState?: {
    connectionStatus: "active" | "ended" | "blocked" | null;
    blockedByMe: boolean;
    blockedByThem: boolean;
  } | null;
  activitySessionData?: {
    id: number;
    title: string;
    activityId: string;
    activityName: string;
    activityIcon: string;
    creatorId: string;
    creatorName: string | null;
    creatorUsername: string | null;
    creatorImage: string | null;
  } | null;
  eventOccurrenceData?: {
    eventName: string;
    eventSlug: string;
    hiiveName: string;
    hiiveSlug: string;
    eventImageUrl: string | null;
    /** The banner's first media; absent on rows cached before it existed. */
    eventMedia?: MessageShareMedia | null;
    hiiveLogoUrl: string | null;
    slotLabel: string | null;
    eventStatus: string | null;
    occurrenceStatus: string | null;
  } | null;
  unreadMentionCount?: number;
  /**
   * Others typing in this chat as of the fetch (DMs and small groups only).
   * Live changes arrive as `chat.typing` on the user channel — read both
   * through useInboxTyping, never this field alone.
   */
  typingUsers?: Array<{
    userId: string;
    name: string | null;
    expiresAt: number;
  }>;
  lastMessage: {
    id: number;
    content: string | null;
    authorId: string;
    authorName: string | null;
    authorUsername?: string | null;
    authorImage?: string | null;
    type: "user" | "system";
    kind?: MessageKind;
    payload?: MessagePayload | null;
    status?: "sent" | "edited" | "deleted";
    createdAt: Date;
  } | null;
  lastMessageKind:
    | "none"
    | "text"
    | "share"
    | "poll"
    | "gif"
    | "sticker"
    | "location"
    | "song"
    | "image"
    | "video"
    | "voice"
    | "mixed"
    | "system"
    // The newest message was unsent: preview reads "This message was deleted".
    | "deleted";
  /**
   * A reaction newer than `lastMessage` — by the viewer, or on the viewer's
   * own message — that the row previews instead ("Reacted 👍 to …"). Read it
   * through `isChatListReactionCurrent`: a later send takes the line back.
   */
  lastReaction?: {
    emoji: string;
    reactorId: string;
    reactorName: string | null;
    createdAt: Date;
    messageId: number;
    messageContent: string | null;
    messageKind: MessageKind;
    messageAttachmentKinds: Array<"image" | "video" | "voice">;
    messageViewOnce: boolean;
  } | null;
  readState: ChatListReadState | null;
  unreadCount: number;
  isArchived: boolean;
  pinnedAt: Date | null;
  mutedUntil?: Date | null;
  isGroupLeft?: boolean;
  updatedAt: Date;
}

export type { ChatSearchTarget };

const normalizeChatList = (rows: unknown[]): ChatListItem[] =>
  rows.map((row) => {
    const item = row as ChatListItem;
    return {
      ...item,
      lastMessage: item.lastMessage
        ? {
            ...item.lastMessage,
            createdAt: coerceDate(item.lastMessage.createdAt),
          }
        : null,
      lastReaction: item.lastReaction
        ? {
            ...item.lastReaction,
            createdAt: coerceDate(item.lastReaction.createdAt),
          }
        : null,
      pinnedAt: item.pinnedAt ? coerceDate(item.pinnedAt) : null,
      mutedUntil: item.mutedUntil ? coerceDate(item.mutedUntil) : null,
      presence: item.presence
        ? {
            ...item.presence,
            lastActiveAt: item.presence.lastActiveAt
              ? coerceDate(item.presence.lastActiveAt)
              : null,
          }
        : null,
      readState: item.readState
        ? {
            ...item.readState,
            lastSeenAt: item.readState.lastSeenAt
              ? coerceDate(item.readState.lastSeenAt)
              : null,
          }
        : null,
      updatedAt: coerceDate(item.updatedAt),
    };
  });

const sortChats = (rows: ChatListItem[]) =>
  [...rows].sort((a, b) => {
    if (a.pinnedAt && !b.pinnedAt) return -1;
    if (!a.pinnedAt && b.pinnedAt) return 1;
    if (a.pinnedAt && b.pinnedAt) {
      const pinDiff = b.pinnedAt.getTime() - a.pinnedAt.getTime();
      if (pinDiff !== 0) return pinDiff;
    }
    return chatListActivityAt(b).getTime() - chatListActivityAt(a).getTime();
  });

type ChatCollectionsSnapshot = {
  all?: ChatListItem[];
  archived?: ChatListItem[];
  totalUnread?: number;
};

const normalizeChatRow = (row: ChatListItem): ChatListItem => ({
  ...row,
  pinnedAt: row.pinnedAt ? coerceDate(row.pinnedAt) : null,
  mutedUntil: row.mutedUntil ? coerceDate(row.mutedUntil) : null,
  presence: row.presence
    ? {
        ...row.presence,
        lastActiveAt: row.presence.lastActiveAt
          ? coerceDate(row.presence.lastActiveAt)
          : null,
      }
    : null,
  readState: row.readState
    ? {
        ...row.readState,
        lastSeenAt: row.readState.lastSeenAt
          ? coerceDate(row.readState.lastSeenAt)
          : null,
      }
    : null,
  updatedAt: coerceDate(row.updatedAt),
  lastMessage: row.lastMessage
    ? {
        ...row.lastMessage,
        createdAt: coerceDate(row.lastMessage.createdAt),
      }
    : null,
  lastReaction: row.lastReaction
    ? {
        ...row.lastReaction,
        createdAt: coerceDate(row.lastReaction.createdAt),
      }
    : null,
});

const snapshotChatCollections = (
  queryClient: ReturnType<typeof useQueryClient>,
): ChatCollectionsSnapshot => ({
  all: queryClient.getQueryData<ChatListItem[]>(chatKeys.userChats("all")),
  archived: queryClient.getQueryData<ChatListItem[]>(
    chatKeys.userChats("archived"),
  ),
  totalUnread: queryClient.getQueryData<number>(chatKeys.totalUnreadCount()),
});

const restoreChatCollections = (
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot?: ChatCollectionsSnapshot,
) => {
  if (!snapshot) return;
  if (snapshot.all) {
    queryClient.setQueryData(chatKeys.userChats("all"), snapshot.all);
  }
  if (snapshot.archived) {
    queryClient.setQueryData(chatKeys.userChats("archived"), snapshot.archived);
  }
  if (typeof snapshot.totalUnread === "number") {
    queryClient.setQueryData(chatKeys.totalUnreadCount(), snapshot.totalUnread);
  }
};

const patchChatCollections = (
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: number,
  updater: (chat: ChatListItem) => ChatListItem | null,
): ChatCollectionsSnapshot => {
  const snapshot = snapshotChatCollections(queryClient);
  const combined = new Map<number, ChatListItem>();

  for (const row of snapshot.all ?? []) {
    combined.set(row.id, normalizeChatRow(row));
  }
  for (const row of snapshot.archived ?? []) {
    if (!combined.has(row.id)) {
      combined.set(row.id, normalizeChatRow(row));
    }
  }

  const existing = combined.get(chatId);
  if (!existing) return snapshot;

  const next = updater(existing);
  if (!next) {
    combined.delete(chatId);
  } else {
    combined.set(chatId, normalizeChatRow(next));
  }

  const allRows = sortChats(
    Array.from(combined.values()).filter((row) => !row.isArchived),
  );
  const archivedRows = sortChats(
    Array.from(combined.values()).filter((row) => row.isArchived),
  );

  if (snapshot.all) {
    queryClient.setQueryData(chatKeys.userChats("all"), allRows);
  }
  if (snapshot.archived) {
    queryClient.setQueryData(chatKeys.userChats("archived"), archivedRows);
  }

  return snapshot;
};

/**
 * A Hiive bot reply is stored under the person who asked (so their chat
 * permissions apply), but it is the bot speaking: every "is this mine?" and
 * "who wrote this?" check should see the bot. Remapping here, where every
 * fetched or sent message passes, keeps those checks untouched.
 */
const HIIVE_BOT_AUTHOR = {
  authorId: HIIVE_BOT_AUTHOR_ID,
  authorName: HIIVE_BOT_NAME,
  authorUsername: null,
  authorImage: null,
} as const;

const normalizeMessages = (rows: unknown[]): Message[] =>
  rows.map((row) => {
    const message = row as Message;
    return {
      ...message,
      ...(isHiiveBotHandle(message.assistantHandle) ? HIIVE_BOT_AUTHOR : {}),
      createdAt: coerceDate(message.createdAt),
      updatedAt: message.updatedAt ? coerceDate(message.updatedAt) : null,
      expiresAt: message.expiresAt ? coerceDate(message.expiresAt) : null,
      editedAt: message.editedAt ? coerceDate(message.editedAt) : null,
      pinnedAt: isActivePin({
        pinnedAt: message.pinnedAt ? coerceDate(message.pinnedAt) : null,
        pinnedExpiresAt: message.pinnedExpiresAt
          ? coerceDate(message.pinnedExpiresAt)
          : null,
      })
        ? coerceDate(message.pinnedAt)
        : null,
      pinnedExpiresAt: message.pinnedExpiresAt
        ? coerceDate(message.pinnedExpiresAt)
        : null,
      poll: message.poll
        ? {
            ...message.poll,
            closesAt: message.poll.closesAt
              ? coerceDate(message.poll.closesAt)
              : null,
          }
        : (message.poll ?? null),
      readState: message.readState
        ? {
            ...message.readState,
            lastSeenAt: message.readState.lastSeenAt
              ? coerceDate(message.readState.lastSeenAt)
              : null,
          }
        : null,
      // Guarantee the Message contract: the delta endpoint can omit `reactions`
      // (the full sync always includes it), which left it `undefined` on a
      // just-sent message and crashed any consumer doing `reactions.length`.
      reactions: message.reactions ?? [],
      attachments: (message.attachments ?? []).map((attachment) => ({
        ...attachment,
        voicePlayback: attachment.voicePlayback
          ? {
              ...attachment.voicePlayback,
              lastPlayedAt: attachment.voicePlayback.lastPlayedAt
                ? coerceDate(attachment.voicePlayback.lastPlayedAt)
                : null,
            }
          : null,
      })),
    };
  });

const mergeMessages = (previous: Message[], updates: Message[]) => {
  const getMessageIdentity = (message: Message) =>
    message.clientMessageId
      ? `client:${message.clientMessageId}`
      : `server:${message.id}`;

  if (updates.length === 0) return previous;
  const byId = new Map(
    previous.map((message) => [getMessageIdentity(message), message]),
  );
  for (const message of updates) {
    byId.set(getMessageIdentity(message), message);
  }
  return Array.from(byId.values()).sort(
    (a, b) => a.createdAt.getTime() - b.createdAt.getTime() || a.id - b.id,
  );
};

export function useHiiveChat(hiiveId: number) {
  return useQuery({
    queryKey: chatKeys.hiiveChat(hiiveId),
    queryFn: () => getOrCreateHiiveChat(hiiveId),
    staleTime: 10 * 60 * 1000,
    refetchOnMount: false,
    enabled: !!hiiveId,
  });
}

export function useChatSearchTargets(query: string, enabled: boolean) {
  const normalizedQuery = query.trim().toLowerCase();

  return useQuery({
    queryKey: chatKeys.searchTargets(normalizedQuery),
    queryFn: async () => {
      const result = await searchChatTargets({
        query: normalizedQuery,
        limit: 8,
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result.data ?? [];
    },
    enabled: enabled && normalizedQuery.length > 0,
    staleTime: 20_000,
    placeholderData: keepPreviousData,
  });
}

export function useOpenHiiveChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (hiiveId: number) =>
      throwIfActionError(await getOrCreateHiiveChat(hiiveId)),
    onSettled: async () => {
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useChatMessages(chatId: number | null | undefined) {
  const queryClient = useQueryClient();
  const realtimeStatus = useRealtimeStatus();
  const pollIterationRef = useRef(0);
  // Read receipts live on OLD rows: someone reading a message bumps their
  // cursor, not the message's `updatedAt`, so a delta can never carry the new
  // "Seen". A `chat.seen` event therefore asks for one full sync.
  const forceFullSyncRef = useRef(false);
  const receiptRevisionRef = useRef(0);
  const syncChatRef = useRef(chatId);

  const query = useQuery({
    queryKey: chatKeys.chatMessages(chatId ?? -1),
    queryFn: async () => {
      if (!chatId) return [];
      if (syncChatRef.current !== chatId) {
        syncChatRef.current = chatId;
        pollIterationRef.current = 0;
        forceFullSyncRef.current = true;
        receiptRevisionRef.current += 1;
      }

      const queryKey = chatKeys.chatMessages(chatId);
      const previous = normalizeMessages(
        queryClient.getQueryData<Message[]>(queryKey) ?? [],
      );
      // Merge onto the cache as it is when the response LANDS, never onto
      // `previous`. A send made while this request was in the air writes its
      // optimistic bubble (and then the server row) into the cache; returning
      // `previous + delta` replaced the cache with a list from before that
      // send, so your message vanished until a later sync brought it back.
      // On a slow link the request is in the air most of the time.
      const mergeOntoLatest = (updates: Message[]) =>
        mergeMessages(
          normalizeMessages(
            queryClient.getQueryData<Message[]>(queryKey) ?? [],
          ),
          updates,
        );

      const shouldFullSync =
        previous.length === 0 ||
        forceFullSyncRef.current ||
        pollIterationRef.current % 20 === 0;
      pollIterationRef.current += 1;

      if (shouldFullSync) {
        let revision = receiptRevisionRef.current;
        let full = await chatQueryFns.chatMessages(chatId);
        // A receipt/recovery during the initial request cannot cancel a query
        // that has no cached data yet. Reconcile that race once immediately.
        if (
          syncChatRef.current === chatId &&
          revision !== receiptRevisionRef.current
        ) {
          revision = receiptRevisionRef.current;
          full = await chatQueryFns.chatMessages(chatId);
        }
        if (
          syncChatRef.current === chatId &&
          revision === receiptRevisionRef.current
        )
          forceFullSyncRef.current = false;
        return mergeOntoLatest(normalizeMessages(full));
      }

      // The cursor must be a SERVER row. An optimistic or failed bubble (id < 0)
      // at the tail would send a negative id, which the delta schema rejects —
      // every sync then throws until that bubble resolves.
      let lastMessage: Message | undefined;
      for (let i = previous.length - 1; i >= 0; i--) {
        const entry = previous[i];
        if (entry && entry.id > 0) {
          lastMessage = entry;
          break;
        }
      }
      // v2 cursor: the newest updatedAt we've seen — server returns older
      // messages mutated after this (reactions, votes, redactions).
      let maxUpdatedAtMs = 0;
      for (const entry of previous) {
        if (entry.id <= 0) continue;
        const updatedMs = entry.updatedAt?.getTime() ?? 0;
        if (updatedMs > maxUpdatedAtMs) maxUpdatedAtMs = updatedMs;
      }
      const delta = await chatQueryFns.chatMessagesDelta({
        chatId,
        afterCreatedAt: lastMessage?.createdAt,
        afterMessageId: lastMessage?.id,
        afterUpdatedAt:
          maxUpdatedAtMs > 0 ? new Date(maxUpdatedAtMs) : undefined,
        limit: 200,
      });

      return mergeOntoLatest(normalizeMessages(delta));
    },
    staleTime: 1_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () =>
      resolveAdaptivePollMs(
        MESSAGE_POLL_MS_ACTIVE,
        MESSAGE_POLL_MS_BACKGROUND,
        !!chatId,
        realtimeStatus,
      ),
    refetchIntervalInBackground: true,
    enabled: !!chatId,
  });

  // A second recovery can arrive during the bounded second full request.
  // Continue after its successful commit rather than waiting for fallback polling.
  useEffect(() => {
    if (
      chatId &&
      query.isSuccess &&
      query.dataUpdatedAt > 0 &&
      !query.isFetching &&
      forceFullSyncRef.current
    ) {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
    }
  }, [
    chatId,
    query.isSuccess,
    query.isFetching,
    query.dataUpdatedAt,
    queryClient,
  ]);

  // Realtime: push-driven refresh while the chat is open. Events trigger the
  // existing delta query (one serialization path); the slow fallback poll above
  // backstops any missed event.
  useRealtimeChannel(chatId ? chatChannel(chatId) : null, (event) => {
    if (!chatId) return;
    // A receipt changes rows the delta query would not return (their
    // updatedAt never moved), so it asks for one full sync.
    const isReceipt =
      event.type === "chat.seen" || event.type === "chat.delivered";
    if (isReceipt) {
      receiptRevisionRef.current += 1;
      forceFullSyncRef.current = true;
    }
    if (
      event.type === "chat.message" ||
      event.type === "chat.message.updated" ||
      isReceipt
    ) {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
      if (!isReceipt)
        void queryClient.invalidateQueries({
          queryKey: chatKeys.pinnedMessages(chatId),
        });
    }
    if (event.type === "chat.message.updated" || event.type === "chat.seen") {
      // Refresh any open reaction / seen-by popups + poll-voter lists.
      void queryClient.invalidateQueries({ queryKey: ["chat", "message"] });
      void queryClient.invalidateQueries({ queryKey: ["chat", "poll-voters"] });
    }
  });

  // Receipt cursors do not change message.updatedAt; recovery needs a full
  // receipt sync, including the first socket connection and native resume.
  const recoverReceipts = () => {
    if (!chatId) return;
    receiptRevisionRef.current += 1;
    forceFullSyncRef.current = true;
    void queryClient.invalidateQueries({
      queryKey: chatKeys.chatMessages(chatId),
    });
  };
  useRealtimeReconnect(recoverReceipts);
  useAppForeground(recoverReceipts);
  const recoverRef = useRef(recoverReceipts);
  recoverRef.current = recoverReceipts;
  useEffect(() => {
    if (chatId) recoverRef.current();
  }, [chatId]);
  useEffect(
    () =>
      onlineManager.subscribe((online) => {
        if (online) recoverRef.current();
      }),
    [],
  );

  return query;
}

const OLDER_MESSAGES_PAGE_SIZE = 60;

/**
 * Scroll-back paging for a thread. Older pages merge into the SAME cache entry
 * as the live window (mergeMessages never evicts), so polls, deltas and
 * optimistic sends keep working over the whole loaded history.
 *
 * `beforeApply` runs right before the older rows land in the cache — after the
 * network wait — so the caller can capture its reading position at the last
 * possible moment and restore it after the prepend commits.
 */
export function useOlderChatMessages(chatId: number | null | undefined) {
  const queryClient = useQueryClient();
  const [state, setState] = useState({ isLoading: false, hasMore: true });
  const inFlightRef = useRef(false);
  const hasMoreRef = useRef(true);

  // A different thread starts with unknown history again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed by chatId
  useEffect(() => {
    inFlightRef.current = false;
    hasMoreRef.current = true;
    setState({ isLoading: false, hasMore: true });
  }, [chatId]);

  const loadOlder = useCallback(
    async (options?: { beforeApply?: () => void }) => {
      if (!chatId || inFlightRef.current || !hasMoreRef.current) return false;
      const queryKey = chatKeys.chatMessages(chatId);
      const current = queryClient.getQueryData<Message[]>(queryKey) ?? [];
      const oldest = current.find((entry) => entry.id > 0);
      if (!oldest) return false;

      inFlightRef.current = true;
      setState((previous) => ({ ...previous, isLoading: true }));
      try {
        const page = await chatQueryFns.chatMessagesBefore({
          chatId,
          beforeCreatedAt: oldest.createdAt,
          beforeMessageId: oldest.id,
          limit: OLDER_MESSAGES_PAGE_SIZE,
        });
        hasMoreRef.current = page.hasMore && page.messages.length > 0;
        if (page.messages.length > 0) {
          options?.beforeApply?.();
          queryClient.setQueryData<Message[]>(queryKey, (old) =>
            mergeMessages(
              normalizeMessages(old ?? []),
              normalizeMessages(page.messages),
            ),
          );
        }
        return page.messages.length > 0;
      } catch {
        // Scroll-back is opportunistic; the next scroll near the top retries.
        return false;
      } finally {
        inFlightRef.current = false;
        setState({ isLoading: false, hasMore: hasMoreRef.current });
      }
    },
    [chatId, queryClient],
  );

  return { loadOlder, ...state };
}

/** A location the share sheet is about to send (one-time or live). */
export type ChatLocationDraft = {
  lat: number;
  lng: number;
  accuracy?: number;
  label?: string;
  address?: string;
  /** Set → live location for this many minutes (15, 60 or 480). */
  liveMinutes?: LiveLocationDurationMinutes;
};

export const MY_LIVE_LOCATIONS_KEY = [
  "chat",
  "live-locations",
  "mine",
] as const;

/** Live locations I'm sharing right now, across all chats. */
export function useMyActiveLiveLocations(enabled = true) {
  return useQuery({
    queryKey: MY_LIVE_LOCATIONS_KEY,
    queryFn: async () => (await getMyActiveLiveLocations()).data ?? [],
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useStopLiveLocation(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: number) =>
      throwIfActionError(await stopLiveLocation({ messageId })),
    onMutate: (messageId) => {
      // The card flips to "ended" on tap, not a round trip later.
      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) =>
            message.id === messageId && isLiveLocationPayload(message.payload)
              ? {
                  ...message,
                  payload: { ...message.payload, endedAt: Date.now() },
                }
              : message,
          ),
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Couldn’t stop sharing",
      );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MY_LIVE_LOCATIONS_KEY }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function useSendMessage(
  chatId: number,
  userId: string,
  userName?: string | null,
  userImage?: string | null,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["sendMessage", chatId],
    mutationFn: ({
      content,
      replyToId,
      clientMessageId,
      attachments,
      kind,
      gif,
      poll,
      location,
      song,
      viewOnce,
      linkPreview,
      suppressLinkPreview,
    }: {
      content: string;
      /**
       * The picked song. Only `provider` + `songId` reach the server, which
       * re-reads it from the catalog; the rest paints the optimistic card.
       */
      song?: MusicAttachment;
      replyToId?: number;
      clientMessageId: string;
      linkPreview?: SignedLinkPreview;
      suppressLinkPreview?: boolean;
      attachments?: Array<{
        kind: "image" | "video" | "voice";
        asset: MediaAssetSlim;
        durationMs?: number;
        waveform?: number[];
      }>;
      kind?: "text" | "gif" | "sticker";
      gif?: {
        provider: "klipy";
        slug: string;
        urls: { gif?: string; webp?: string; mp4?: string };
        width: number;
        height: number;
      };
      poll?: {
        question: string;
        options: string[];
        allowsMultiple: boolean;
        isAnonymous: boolean;
        durationHours?: number;
      };
      location?: ChatLocationDraft;
      viewOnce?: boolean;
    }) =>
      sendMessage({
        chatId,
        content,
        replyToId,
        clientMessageId,
        attachments,
        kind,
        gif,
        poll,
        location,
        song: song ? musicAttachmentInput(song) : undefined,
        viewOnce,
        linkPreview,
        suppressLinkPreview,
      }).then((result) => {
        if (result.error) {
          throw new Error(result.error);
        }
        return result;
      }),

    onMutate: async (vars) => {
      await queryClient.cancelQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
      const previous =
        queryClient.getQueryData<Message[]>(chatKeys.chatMessages(chatId)) ??
        [];

      const optimistic: Message = {
        id: -Date.now(),
        clientMessageId: vars.clientMessageId,
        content: vars.song
          ? "🎵 Song"
          : vars.content ||
            (vars.poll
              ? `📊 ${vars.poll.question}`
              : vars.location
                ? vars.location.liveMinutes
                  ? "📍 Live location"
                  : "📍 Location"
                : vars.viewOnce
                  ? vars.attachments?.[0]?.kind === "video"
                    ? "View once video"
                    : "View once photo"
                  : vars.gif
                    ? vars.kind === "sticker"
                      ? "Sticker"
                      : "GIF"
                    : vars.content),
        kind: vars.song
          ? "song"
          : vars.poll
            ? "poll"
            : vars.location
              ? "location"
              : vars.gif
                ? vars.kind === "sticker"
                  ? "sticker"
                  : "gif"
                : "text",
        // The composer's preview rides in the optimistic bubble too, so the
        // message appears at its final height and never grows on confirm.
        payload:
          (vars.song ? { song: vars.song } : null) ??
          vars.gif ??
          (vars.location
            ? vars.location.liveMinutes
              ? {
                  lat: vars.location.lat,
                  lng: vars.location.lng,
                  accuracy: vars.location.accuracy ?? null,
                  mode: "live" as const,
                  liveUntil: Date.now() + vars.location.liveMinutes * 60_000,
                  updatedAt: Date.now(),
                  endedAt: null,
                }
              : {
                  lat: vars.location.lat,
                  lng: vars.location.lng,
                  accuracy: vars.location.accuracy ?? null,
                  label: vars.location.label ?? null,
                  address: vars.location.address ?? null,
                  mode: "static" as const,
                }
            : vars.linkPreview && !vars.attachments?.length
              ? { linkPreview: vars.linkPreview.preview }
              : null),
        viewOnce: vars.viewOnce ?? false,
        viewOnceState: vars.viewOnce ? ("sent" as const) : null,
        type: "user",
        authorId: userId,
        authorName: userName ?? "You",
        authorUsername: null,
        authorImage: userImage ?? null,
        replyToId: vars.replyToId ?? null,
        status: "sent",
        createdAt: new Date(),
        editedAt: null,
        reactions: [],
        localStatus: "sending",
        attachmentSummary:
          vars.attachments && vars.attachments.length > 0
            ? {
                count: vars.attachments.length,
                kind:
                  new Set(vars.attachments.map((attachment) => attachment.kind))
                    .size === 1
                    ? (vars.attachments[0]?.kind ?? "mixed")
                    : "mixed",
              }
            : null,
        attachments:
          vars.attachments?.map((attachment, index) => ({
            id: -(Date.now() + index + 1),
            kind: attachment.kind,
            asset: attachment.asset,
            durationMs: attachment.durationMs ?? null,
            waveform: attachment.waveform ?? null,
            voicePlayback:
              attachment.kind === "voice"
                ? {
                    playedByViewer: false,
                    playedByOthersCount: 0,
                    lastPlayedAt: null,
                  }
                : null,
          })) ?? [],
      };

      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) => {
          const current = old ?? [];
          const existingIndex = current.findIndex(
            (message) => message.clientMessageId === vars.clientMessageId,
          );
          if (existingIndex === -1) {
            return [...current, optimistic];
          }

          return current.map((message, index) =>
            index === existingIndex
              ? {
                  ...message,
                  ...optimistic,
                  id: message.id,
                  createdAt: message.createdAt,
                }
              : message,
          );
        },
      );

      return {
        previous,
        tempId: optimistic.id,
        clientMessageId: vars.clientMessageId,
      };
    },

    onError: (error, _vars, context) => {
      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? context?.previous ?? []).map((message) =>
            message.clientMessageId === context?.clientMessageId ||
            message.id === context?.tempId
              ? {
                  ...message,
                  localStatus: "failed",
                }
              : message,
          ),
      );
      toast.error(
        error instanceof Error ? error.message : "Failed to send message",
      );
    },

    onSuccess: (result, _vars, context) => {
      if (!result.data) return;

      const serverMessage = normalizeMessages([result.data])[0];
      if (!serverMessage) return;

      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) =>
            message.clientMessageId === context?.clientMessageId ||
            message.id === context?.tempId
              ? {
                  ...serverMessage,
                  // The send response is a slimmer row than a list read (no
                  // joined poll state, no view-once receipt). Never let it
                  // downgrade what the optimistic bubble already knew — a
                  // missing kind/payload re-renders a GIF as the word "GIF"
                  // until the next delta sync, which reads as a layout jump.
                  kind: serverMessage.kind ?? message.kind,
                  payload: serverMessage.payload ?? message.payload,
                  poll: serverMessage.poll ?? message.poll,
                  viewOnceState:
                    serverMessage.viewOnceState ?? message.viewOnceState,
                  clientMessageId: context?.clientMessageId,
                  localStatus: undefined,
                }
              : message,
          ),
      );
    },

    onSettled: async (_result, _error, vars) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
        invalidateChatCollections(queryClient),
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        queryClient.invalidateQueries({
          queryKey: ["unreadNotificationCount"],
        }),
        queryClient.invalidateQueries({ queryKey: ["native-widget-stats"] }),
        // A new live share: the app-wide sharer starts tracking it.
        vars?.location?.liveMinutes
          ? queryClient.invalidateQueries({ queryKey: MY_LIVE_LOCATIONS_KEY })
          : undefined,
      ]);
    },
  });
}

/**
 * Drops a send that failed and the person chose not to retry. Only ever
 * touches a local bubble (`localStatus === "failed"`) — a delivered message
 * is unsent through useDeleteMessage instead.
 */
export function useDiscardFailedMessage(chatId: number) {
  const queryClient = useQueryClient();
  return useCallback(
    (messageId: number) => {
      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).filter(
            (message) =>
              !(message.id === messageId && message.localStatus === "failed"),
          ),
      );
    },
    [chatId, queryClient],
  );
}

export function useReactToMessage(chatId: number, userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["reactToMessage", chatId],
    mutationFn: ({
      messageId,
      reaction,
    }: {
      messageId: number;
      reaction: string;
    }) =>
      reactToMessage({ messageId, reaction }).then((result) => {
        if (result.error) {
          throw new Error(result.error);
        }
        return result;
      }),

    onMutate: async ({ messageId, reaction }) => {
      await queryClient.cancelQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
      const previous =
        queryClient.getQueryData<Message[]>(chatKeys.chatMessages(chatId)) ??
        [];

      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) => {
            if (message.id !== messageId) return message;
            const reactions = message.reactions ?? [];
            const hasReaction = reactions.some(
              (entry) => entry.userId === userId && entry.reaction === reaction,
            );
            // One reaction per person, like WhatsApp/Instagram: the same
            // emoji again removes it, a different one replaces it.
            const others = reactions.filter((entry) => entry.userId !== userId);

            return {
              ...message,
              reactions: hasReaction
                ? others
                : [...others, { userId, reaction }],
            };
          }),
      );

      return { previous };
    },

    onError: (error, _vars, context) => {
      queryClient.setQueryData(
        chatKeys.chatMessages(chatId),
        context?.previous ?? [],
      );
      toast.error(
        error instanceof Error ? error.message : "Failed to react to message",
      );
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
    },
  });
}

export function useUpdateMessage(chatId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateMessage", chatId],
    mutationFn: async ({
      messageId,
      content,
    }: {
      messageId: number;
      content: string;
    }) =>
      throwIfActionError(
        await updateMessageAction({
          messageId,
          content,
        }),
      ),
    onMutate: async ({ messageId, content }) => {
      await queryClient.cancelQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });

      const previous =
        queryClient.getQueryData<Message[]>(chatKeys.chatMessages(chatId)) ??
        [];

      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  content,
                  status: "edited",
                  editedAt: new Date(),
                }
              : message,
          ),
      );

      return { previous };
    },
    onError: (error, _vars, context) => {
      queryClient.setQueryData(
        chatKeys.chatMessages(chatId),
        context?.previous ?? [],
      );
      toast.error(
        error instanceof Error ? error.message : "Failed to update message",
      );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
        invalidateChatCollections(queryClient),
      ]);
    },
  });
}

export function useDeleteMessage(chatId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["deleteMessage", chatId],
    mutationFn: async (messageId: number) =>
      throwIfActionError(await deleteMessageAction(messageId)),
    onMutate: async (messageId) => {
      await queryClient.cancelQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });

      const previous =
        queryClient.getQueryData<Message[]>(chatKeys.chatMessages(chatId)) ??
        [];

      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  content: null,
                  status: "deleted",
                  attachments: [],
                  reactions: [],
                }
              : message,
          ),
      );

      return { previous };
    },
    onError: (error, _vars, context) => {
      queryClient.setQueryData(
        chatKeys.chatMessages(chatId),
        context?.previous ?? [],
      );
      toast.error(
        error instanceof Error ? error.message : "Failed to delete message",
      );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
        invalidateChatCollections(queryClient),
      ]);
    },
  });
}

export function useMarkChatSeen(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["markChatSeen", chatId],
    mutationFn: async (messageId: number) =>
      throwIfActionError(await markChatSeen({ chatId, messageId })),
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("all") }),
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("archived") }),
        queryClient.cancelQueries({ queryKey: chatKeys.totalUnreadCount() }),
      ]);

      const snapshot = snapshotChatCollections(queryClient);
      let reducedUnread = 0;
      const now = new Date();

      patchChatCollections(queryClient, chatId, (chat) => {
        reducedUnread = chat.unreadCount;
        return {
          ...chat,
          unreadCount: 0,
          updatedAt: now,
        };
      });

      if (reducedUnread > 0 && typeof snapshot.totalUnread === "number") {
        queryClient.setQueryData(
          chatKeys.totalUnreadCount(),
          Math.max(0, snapshot.totalUnread - reducedUnread),
        );
      }

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      restoreChatCollections(queryClient, context?.snapshot);
    },
    onSettled: async () => {
      await Promise.all([
        invalidateChatCollections(queryClient),
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        queryClient.invalidateQueries({
          queryKey: ["unreadNotificationCount"],
        }),
        queryClient.invalidateQueries({ queryKey: ["native-widget-stats"] }),
      ]);
    },
  });
}

export function useMarkVoiceAttachmentPlayed(chatId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["markVoiceAttachmentPlayed", chatId],
    mutationFn: async ({
      messageId,
      attachmentId,
    }: {
      messageId: number;
      attachmentId: number;
    }) =>
      throwIfActionError(
        await markVoiceAttachmentPlayed({ chatId, messageId, attachmentId }),
      ),
    onMutate: async ({ messageId, attachmentId }) => {
      await queryClient.cancelQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });

      const previous =
        queryClient.getQueryData<Message[]>(chatKeys.chatMessages(chatId)) ??
        [];
      const playedAt = new Date();

      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  attachments: message.attachments.map((attachment) =>
                    attachment.id === attachmentId
                      ? {
                          ...attachment,
                          voicePlayback: {
                            playedByViewer: true,
                            playedByOthersCount:
                              attachment.voicePlayback?.playedByOthersCount ??
                              0,
                            lastPlayedAt: playedAt,
                          },
                        }
                      : attachment,
                  ),
                }
              : message,
          ),
      );

      return { previous };
    },
    onError: (_error, _vars, context) => {
      queryClient.setQueryData(
        chatKeys.chatMessages(chatId),
        context?.previous ?? [],
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
    },
  });
}

export function usePendingMessages() {
  return useMutationState<string>({
    filters: { mutationKey: ["sendMessage"], status: "pending" },
    select: (mutation) =>
      (mutation.state.variables as { content: string })?.content,
  });
}

export function useUserChats(
  tab: "all" | "archived" = "all",
  options?: { enabled?: boolean },
) {
  const queryClient = useQueryClient();
  const realtimeStatus = useRealtimeStatus();
  const enabled = options?.enabled ?? true;
  const query = useQuery({
    queryKey: chatKeys.userChats(tab),
    queryFn: async () => {
      const rows = await chatQueryFns.userChats(tab);
      return sortChats(normalizeChatList(rows));
    },
    enabled,
    staleTime: 1_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () =>
      resolveAdaptivePollMs(
        CHAT_LIST_POLL_MS_ACTIVE,
        CHAT_LIST_POLL_MS_BACKGROUND,
        enabled,
        realtimeStatus,
      ),
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });

  // New messages in any chat touch this user's list + unread badge.
  useUserRealtime((event) => {
    if (!enabled) return;
    if (event.type === "chat.list.touch" || event.type === "buddy.status") {
      void queryClient.invalidateQueries({ queryKey: ["userChats"] });
      void queryClient.invalidateQueries({
        queryKey: chatKeys.totalUnreadCount(),
      });
    }
  });
  useRealtimeReconnect(() => {
    if (!enabled) return;
    void queryClient.invalidateQueries({ queryKey: ["userChats"] });
  });

  return query;
}

export function useTotalUnreadCount({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  const queryClient = useQueryClient();
  const realtimeStatus = useRealtimeStatus();
  const query = useQuery({
    queryKey: chatKeys.totalUnreadCount(),
    queryFn: () => chatQueryFns.totalUnreadCount(),
    enabled,
    staleTime: 2_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () =>
      resolveAdaptivePollMs(
        UNREAD_POLL_MS_ACTIVE,
        UNREAD_POLL_MS_BACKGROUND,
        true,
        realtimeStatus,
      ),
    refetchIntervalInBackground: false,
  });

  useUserRealtime((event) => {
    if (!enabled) return;
    if (event.type === "chat.list.touch") {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.totalUnreadCount(),
      });
    }
  });

  return query;
}

/** A "stopped typing" event outranks a stale poll row for this long. */
const INBOX_TYPING_STOP_HOLD_MS = 7_000;

/**
 * Who is typing in each inbox chat, right now.
 *
 * Two sources: `typingUsers` on each list item (true as of the last fetch —
 * with the socket open that can be 30s old) and live `chat.typing` events on
 * the user channel (start/refresh every ~3s, stop). Live wins per person; a
 * stop is remembered briefly so an older poll row can't resurrect it. Every
 * entry carries its own expiry and one timer re-renders at the soonest, so a
 * typist who vanishes (app killed, network gone) clears within ~6s.
 */
export function useInboxTyping(
  chats: ChatListItem[],
): Map<number, Array<{ userId: string; name: string | null }>> {
  const [live, setLive] = useState<
    Map<number, Map<string, { name: string | null; expiresAt: number }>>
  >(() => new Map());
  // 0 until mounted — the inbox can prerender, and a clock read during
  // render is an unstable value there. Live events set it for real.
  const [now, setNow] = useState(0);
  useEffect(() => setNow(Date.now()), []);

  useUserRealtime((event) => {
    if (event.type !== "chat.typing") return;
    const receivedAt = Date.now();
    setNow(receivedAt);
    setLive((previous) => {
      const next = new Map(previous);
      const perChat = new Map(next.get(event.chatId) ?? []);
      const stopped = event.expiresAt <= receivedAt;
      perChat.set(event.userId, {
        name: event.name ?? perChat.get(event.userId)?.name ?? null,
        // A stop is stored as an already-expired entry that still shadows
        // the poll row until the hold lapses.
        expiresAt: stopped
          ? receivedAt - INBOX_TYPING_STOP_HOLD_MS
          : event.expiresAt,
      });
      next.set(event.chatId, perChat);
      return next;
    });
  });

  const typing = useMemo(() => {
    const result = new Map<
      number,
      Array<{ userId: string; name: string | null }>
    >();
    for (const chatItem of chats) {
      const people = new Map<string, string | null>();
      const liveForChat = live.get(chatItem.id);
      for (const entry of chatItem.typingUsers ?? []) {
        const liveEntry = liveForChat?.get(entry.userId);
        // A live entry — typing OR a recent stop — decides for this person.
        if (liveEntry && liveEntry.expiresAt > now - INBOX_TYPING_STOP_HOLD_MS)
          continue;
        if (entry.expiresAt > now) people.set(entry.userId, entry.name);
      }
      for (const [userId, entry] of liveForChat ?? []) {
        if (entry.expiresAt > now) people.set(userId, entry.name);
      }
      if (people.size > 0) {
        result.set(
          chatItem.id,
          Array.from(people, ([userId, name]) => ({ userId, name })),
        );
      }
    }
    return result;
  }, [chats, live, now]);

  // Re-render at the next moment anything changes state: a typist expiring
  // or a stop's hold lapsing.
  useEffect(() => {
    let soonest = Number.POSITIVE_INFINITY;
    for (const chatItem of chats) {
      for (const entry of chatItem.typingUsers ?? []) {
        if (entry.expiresAt > now) soonest = Math.min(soonest, entry.expiresAt);
      }
    }
    for (const perChat of live.values()) {
      for (const entry of perChat.values()) {
        const edge =
          entry.expiresAt > now
            ? entry.expiresAt
            : entry.expiresAt + INBOX_TYPING_STOP_HOLD_MS;
        if (edge > now) soonest = Math.min(soonest, edge);
      }
    }
    if (!Number.isFinite(soonest)) return;
    const timeoutId = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(50, soonest - Date.now() + 20),
    );
    return () => window.clearTimeout(timeoutId);
  }, [chats, live, now]);

  return typing;
}

export function useArchiveChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chatId: number) =>
      throwIfActionError(await archiveChatForUser({ chatId })),
    onMutate: async (chatId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("all") }),
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("archived") }),
      ]);
      const snapshot = patchChatCollections(queryClient, chatId, (chat) => ({
        ...chat,
        isArchived: true,
        updatedAt: new Date(),
      }));
      return { snapshot };
    },
    onError: (_error, _chatId, context) => {
      restoreChatCollections(queryClient, context?.snapshot);
    },
    onSettled: async () => {
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useUnarchiveChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chatId: number) =>
      throwIfActionError(await unarchiveChatForUser({ chatId })),
    onMutate: async (chatId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("all") }),
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("archived") }),
      ]);
      const snapshot = patchChatCollections(queryClient, chatId, (chat) => ({
        ...chat,
        isArchived: false,
        updatedAt: new Date(),
      }));
      return { snapshot };
    },
    onError: (_error, _chatId, context) => {
      restoreChatCollections(queryClient, context?.snapshot);
    },
    onSettled: async () => {
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useDeleteChatForUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chatId: number) =>
      throwIfActionError(await deleteChatForUser({ chatId })),
    onMutate: async (chatId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("all") }),
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("archived") }),
        queryClient.cancelQueries({ queryKey: chatKeys.chatMessages(chatId) }),
      ]);

      const snapshot = patchChatCollections(queryClient, chatId, () => null);
      const previousMessages =
        queryClient.getQueryData<Message[]>(chatKeys.chatMessages(chatId)) ??
        [];
      queryClient.setQueryData(chatKeys.chatMessages(chatId), []);

      return { snapshot, previousMessages };
    },
    onError: (_error, chatId, context) => {
      restoreChatCollections(queryClient, context?.snapshot);
      queryClient.setQueryData(
        chatKeys.chatMessages(chatId),
        context?.previousMessages ?? [],
      );
    },
    onSettled: async () => {
      await invalidateChatCollections(queryClient);
    },
  });
}

export function usePinChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      chatId,
      pinned,
    }: {
      chatId: number;
      pinned: boolean;
    }) => throwIfActionError(await pinChatForUser({ chatId, pinned })),
    onMutate: async ({ chatId, pinned }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("all") }),
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("archived") }),
      ]);
      const snapshot = patchChatCollections(queryClient, chatId, (chat) => ({
        ...chat,
        pinnedAt: pinned ? new Date() : null,
        updatedAt: new Date(),
      }));
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      restoreChatCollections(queryClient, context?.snapshot);
    },
    onSettled: async () => {
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useMuteChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      chatId,
      mutedUntil,
    }: {
      chatId: number;
      mutedUntil?: Date | null;
    }) => throwIfActionError(await muteChatForUser({ chatId, mutedUntil })),
    onMutate: async ({ chatId, mutedUntil }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("all") }),
        queryClient.cancelQueries({ queryKey: chatKeys.userChats("archived") }),
      ]);
      const snapshot = patchChatCollections(queryClient, chatId, (chat) => ({
        ...chat,
        mutedUntil: mutedUntil ?? null,
        updatedAt: new Date(),
      }));
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      restoreChatCollections(queryClient, context?.snapshot);
    },
    onSettled: async () => {
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useStartDirectChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (targetUserId: string) =>
      throwIfActionError(await startDirectChat({ targetUserId })),
    onSettled: async () => {
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useCreateGroupChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      participantUserIds: string[];
      avatar?: MediaAssetSlim;
    }) => throwIfActionError(await createGroupChat(payload)),
    onSuccess: () => {
      void invalidateChatCollections(queryClient);
    },
  });
}

export function useRenameGroupChat(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      throwIfActionError(await renameGroupChat({ chatId, name })),
    onSettled: async () => {
      await Promise.all([
        invalidateChatCollections(queryClient),
        queryClient.invalidateQueries({
          queryKey: chatKeys.groupMembers(chatId),
        }),
      ]);
    },
  });
}

export function useLeaveGroupChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chatId: number) =>
      throwIfActionError(await leaveGroupChat({ chatId })),
    onSettled: async (_data, _error, chatId) => {
      await Promise.all([
        invalidateChatCollections(queryClient),
        queryClient.invalidateQueries({
          queryKey: chatKeys.groupMembers(chatId),
        }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function useAddGroupMembers(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userIds: string[]) =>
      throwIfActionError(await addGroupMembers({ chatId, userIds })),
    onSettled: async () => {
      await Promise.all([
        invalidateChatCollections(queryClient),
        queryClient.invalidateQueries({
          queryKey: chatKeys.groupMembers(chatId),
        }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function useRemoveGroupMember(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      throwIfActionError(await removeGroupMember({ chatId, userId })),
    onSettled: async () => {
      await Promise.all([
        invalidateChatCollections(queryClient),
        queryClient.invalidateQueries({
          queryKey: chatKeys.groupMembers(chatId),
        }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function usePromoteGroupAdmin(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      throwIfActionError(await promoteGroupAdmin({ chatId, userId })),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: chatKeys.groupMembers(chatId),
        }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function useDemoteGroupAdmin(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      throwIfActionError(await demoteGroupAdmin({ chatId, userId })),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: chatKeys.groupMembers(chatId),
        }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function useGroupMembers(chatId: number | null | undefined) {
  const queryClient = useQueryClient();
  const realtimeStatus = useRealtimeStatus();
  const query = useQuery({
    queryKey: chatKeys.groupMembers(chatId ?? -1),
    queryFn: async () => {
      if (!chatId) return [] as GroupMember[];
      const rows = await chatQueryFns.groupMembers(chatId);
      return rows.map((row) => ({
        ...row,
        leftAt: row.leftAt ? coerceDate(row.leftAt) : null,
      })) as GroupMember[];
    },
    enabled: !!chatId,
    staleTime: 2_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () =>
      resolveAdaptivePollMs(
        CHAT_LIST_POLL_MS_ACTIVE,
        CHAT_LIST_POLL_MS_BACKGROUND,
        !!chatId,
        realtimeStatus,
      ),
    refetchIntervalInBackground: false,
  });

  useRealtimeChannel(chatId ? chatChannel(chatId) : null, (event) => {
    if (chatId && event.type === "chat.members") {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.groupMembers(chatId),
      });
    }
  });
  useRealtimeReconnect(() => {
    if (chatId) {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.groupMembers(chatId),
      });
    }
  });

  return query;
}

export function useUsersPresence(userIds: string[]) {
  const queryClient = useQueryClient();
  const realtimeStatus = useRealtimeStatus();
  const sortedUserIds = useMemo(
    () => [...userIds].filter(Boolean).sort(),
    [userIds],
  );
  const presenceChannels = useMemo(
    () => sortedUserIds.map((userId) => presenceChannel(userId)),
    [sortedUserIds],
  );

  const query = useQuery({
    queryKey: chatKeys.usersPresence(sortedUserIds),
    queryFn: async () => {
      if (sortedUserIds.length === 0)
        return [] as Array<{
          userId: string;
          lastActiveAt: Date | null;
          isActiveNow: boolean;
          label: string | null;
          kind: "active" | "recent" | "offline";
        }>;
      const rows = await chatQueryFns.usersPresence(sortedUserIds);
      return rows.map((row) => ({
        ...row,
        lastActiveAt: row.lastActiveAt ? coerceDate(row.lastActiveAt) : null,
      }));
    },
    enabled: sortedUserIds.length > 0,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () =>
      resolveAdaptivePollMs(
        PRESENCE_POLL_MS_ACTIVE,
        PRESENCE_POLL_MS_BACKGROUND,
        sortedUserIds.length > 0,
        realtimeStatus,
      ),
    refetchIntervalInBackground: false,
  });

  useRealtimeChannels(
    presenceChannels,
    (event) => {
      // They changed their last-seen setting: the server decides what we may
      // now see, so ask it rather than patch.
      if (event.type === "chat.presence.settings") {
        if (!sortedUserIds.includes(event.userId)) return;
        void queryClient.invalidateQueries({
          queryKey: chatKeys.usersPresence(sortedUserIds),
        });
        return;
      }
      if (event.type !== "chat.presence") return;
      if (!sortedUserIds.includes(event.userId)) return;

      // A heartbeat only says "online now"; the exact time stays with the
      // server, which applies the last-seen rule on the next fetch.
      const state = {
        ...resolveChatPresenceState(Date.now()),
        lastActiveAt: null,
        observedAt: Date.now(),
      };
      queryClient.setQueryData<
        Array<{
          userId: string;
          lastActiveAt: Date | null;
          isActiveNow: boolean;
          label: string | null;
          kind: "active" | "recent" | "offline";
        }>
      >(chatKeys.usersPresence(sortedUserIds), (old) => {
        const byUserId = new Map(
          (old ?? []).map((row) => [row.userId, row] as const),
        );
        byUserId.set(event.userId, { userId: event.userId, ...state });
        return sortedUserIds.map(
          (userId) =>
            byUserId.get(userId) ?? {
              userId,
              ...resolveChatPresenceState(null),
            },
        );
      });
    },
    sortedUserIds.length > 0,
  );
  useRealtimeReconnect(() => {
    if (sortedUserIds.length > 0) {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.usersPresence(sortedUserIds),
      });
    }
  });

  return query;
}

export function useTypingUsers(chatId: number | null | undefined) {
  const queryClient = useQueryClient();
  const realtimeStatus = useRealtimeStatus();
  const query = useQuery({
    queryKey: chatKeys.chatTypingUsers(chatId ?? -1),
    queryFn: async () => {
      if (!chatId) return [] as ChatTypingUser[];
      const rows = await chatQueryFns.typingUsers(chatId);
      return rows.map((row) => ({
        ...row,
        expiresAt: coerceDate(row.expiresAt),
      })) as ChatTypingUser[];
    },
    enabled: !!chatId,
    staleTime: 500,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () => {
      // Typing entries expire fast; keep a short safety poll even when the
      // socket is live so a missed "stopped typing" clears within seconds.
      if (realtimeStatus === "open") {
        return chatId ? 5_000 : false;
      }
      return resolveAdaptivePollMs(
        TYPING_POLL_MS_ACTIVE,
        TYPING_POLL_MS_BACKGROUND,
        !!chatId,
        realtimeStatus,
      );
    },
    refetchIntervalInBackground: true,
  });

  useRealtimeChannel(chatId ? chatChannel(chatId) : null, (event) => {
    if (event.type === "chat.typing" && chatId) {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.chatTypingUsers(chatId),
      });
    }
  });

  return query;
}

export function useSetTyping(chatId: number) {
  return useMutation({
    mutationFn: (isTyping: boolean) => setTyping({ chatId, isTyping }),
  });
}

export function useMessageSeenBy(messageId: number | null | undefined) {
  const realtimeStatus = useRealtimeStatus();
  return useQuery({
    queryKey: chatKeys.chatSeenBy(messageId ?? -1),
    queryFn: async () => {
      if (!messageId) return [] as SeenByUser[];
      const rows = await chatQueryFns.messageSeenBy(messageId);
      return rows.map((row) => ({
        ...row,
        seenAt: row.seenAt ? coerceDate(row.seenAt) : null,
      })) as SeenByUser[];
    },
    enabled: !!messageId,
    staleTime: 2_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () =>
      resolveAdaptivePollMs(5_000, 10_000, !!messageId, realtimeStatus),
    refetchIntervalInBackground: true,
  });
}

export type ShareEntityType =
  | "post"
  | "event"
  | "venue"
  | "hiive"
  | "story"
  | "profile"
  | "activity_post"
  | "activity_session"
  | "movement_activity"
  | "movement_route"
  | "weight_training_workout"
  | "weight_training_routine"
  | "event_booking_attendee"
  | "hiive_membership"
  | "shop_item";

export function useShareToChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entityType: ShareEntityType;
      entityId: string;
      chatIds: number[];
      comment?: string;
    }) => throwIfActionError(await shareToChatAction(input)),
    onSuccess: async (_result, variables) => {
      await Promise.all(
        variables.chatIds.map((chatId) =>
          queryClient.invalidateQueries({
            queryKey: chatKeys.chatMessages(chatId),
          }),
        ),
      );
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useForwardMessages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      messageIds: number[];
      targetChatIds: number[];
    }) => throwIfActionError(await forwardMessagesAction(input)),
    onSuccess: async (_result, variables) => {
      await Promise.all(
        variables.targetChatIds.map((chatId) =>
          queryClient.invalidateQueries({
            queryKey: chatKeys.chatMessages(chatId),
          }),
        ),
      );
      await invalidateChatCollections(queryClient);
    },
  });
}

export function useVotePoll(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: number; optionIds: number[] }) =>
      throwIfActionError(await votePoll(input)),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
      const previous =
        queryClient.getQueryData<Message[]>(chatKeys.chatMessages(chatId)) ??
        [];

      // Optimistically re-tally: remove my old votes, apply the new ones.
      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) => {
            if (message.id !== vars.messageId || !message.poll) return message;
            const selected = new Set(vars.optionIds);
            const hadVoted = message.poll.options.some(
              (option) => option.votedByViewer,
            );
            const willVote = vars.optionIds.length > 0;
            return {
              ...message,
              poll: {
                ...message.poll,
                totalVoters:
                  message.poll.totalVoters +
                  (willVote ? 1 : 0) -
                  (hadVoted ? 1 : 0),
                options: message.poll.options.map((option) => {
                  const wasVoted = option.votedByViewer;
                  const isVoted = selected.has(option.id);
                  return {
                    ...option,
                    votedByViewer: isVoted,
                    voteCount:
                      option.voteCount + (isVoted ? 1 : 0) - (wasVoted ? 1 : 0),
                  };
                }),
              },
            };
          }),
      );

      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          chatKeys.chatMessages(chatId),
          context.previous,
        );
      }
      toast.error(error instanceof Error ? error.message : "Failed to vote");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
    },
  });
}

export interface PinnedMessage {
  id: number;
  content: string | null;
  kind: MessageKind;
  pinnedAt: Date | null;
  pinnedExpiresAt?: Date | null;
  createdAt: Date;
  authorName: string | null;
}

export function usePinnedMessages(chatId: number, enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: chatKeys.pinnedMessages(chatId),
    queryFn: async () => {
      const result = await getPinnedMessages(chatId);
      if (result.error || !result.data) {
        throw new Error(result.error ?? "Failed to load pinned messages");
      }
      return result.data.map((row) => ({
        ...row,
        pinnedAt: row.pinnedAt ? coerceDate(row.pinnedAt) : null,
        pinnedExpiresAt: row.pinnedExpiresAt
          ? coerceDate(row.pinnedExpiresAt)
          : null,
        createdAt: coerceDate(row.createdAt),
      })) as PinnedMessage[];
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  useEffect(() => {
    const ends = (query.data ?? []).flatMap((pin) =>
      pin.pinnedAt
        ? [
            (
              pin.pinnedExpiresAt ??
              pinExpiresAt(pin.pinnedAt, DEFAULT_PIN_DURATION_SEC)
            ).getTime(),
          ]
        : [],
    );
    if (!ends.length) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const remaining = Math.min(...ends) - Date.now() + 100;
      if (remaining > 0) {
        timer = setTimeout(schedule, Math.min(2_147_483_647, remaining));
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: chatKeys.pinnedMessages(chatId),
      });
      void queryClient.invalidateQueries({
        queryKey: chatKeys.chatMessages(chatId),
      });
    };
    schedule();
    return () => clearTimeout(timer);
  }, [query.data, queryClient, chatId]);
  return query;
}

export function usePinMessage(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      messageId: number;
      pin: boolean;
      durationSec?: number;
    }) =>
      throwIfActionError(
        input.pin
          ? await pinMessage({
              messageId: input.messageId,
              durationSec: input.durationSec,
            })
          : await unpinMessage({ messageId: input.messageId }),
      ),
    onSuccess: (_result, vars) => {
      toast.success(vars.pin ? "Pinned" : "Unpinned");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to update pin",
      );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: chatKeys.pinnedMessages(chatId),
        }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function useChatInfo(chatId: number, enabled = true) {
  return useQuery({
    queryKey: chatKeys.chatInfo(chatId),
    queryFn: () => chatQueryFns.chatInfo(chatId),
    enabled,
    staleTime: 30_000,
  });
}

export interface ChatSearchResult {
  id: number;
  content: string | null;
  kind: MessageKind;
  createdAt: Date;
  authorId: string;
  authorName: string | null;
  authorImage: string | null;
}

export function useChatMessageSearch(chatId: number, query: string) {
  const normalized = query.trim();
  return useQuery({
    queryKey: [...chatKeys.chatMessages(chatId), "search", normalized],
    queryFn: async () => {
      const result = await searchChatMessages({ chatId, query: normalized });
      if (result.error || !result.data) {
        throw new Error(result.error ?? "Search failed");
      }
      return result.data.map((row) => ({
        ...row,
        createdAt: coerceDate(row.createdAt),
      })) as ChatSearchResult[];
    },
    enabled: normalized.length >= 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export interface PollVoterOption {
  id: number;
  label: string;
  position: number;
  voters: Array<{
    userId: string;
    userName: string | null;
    username: string | null;
    userImage: string | null;
    votedAt: Date | null;
  }>;
}

export function usePollVoters(messageId: number | null) {
  return useQuery({
    queryKey: ["chat", "poll-voters", messageId],
    queryFn: async () => {
      if (messageId == null) return { options: [] as PollVoterOption[] };
      const result = await getPollVoters(messageId);
      if (result.error || !result.data) {
        throw new Error(result.error ?? "Failed to load votes");
      }
      return {
        options: result.data.options.map((option) => ({
          ...option,
          voters: option.voters.map((voter) => ({
            ...voter,
            votedAt: voter.votedAt ? coerceDate(voter.votedAt) : null,
          })),
        })),
      };
    },
    enabled: messageId != null,
    staleTime: 5_000,
  });
}

export function useUpdateGroupDescription(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (description: string) =>
      throwIfActionError(await updateGroupDescription({ chatId, description })),
    onSuccess: () => {
      toast.success("Description updated");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to update description",
      );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKeys.chatInfo(chatId) }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
        invalidateChatCollections(queryClient),
      ]);
    },
  });
}

export function useUpdateGroupAvatar(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (asset: {
      id: string;
      key: string;
      order: number;
      posterKey?: string;
    }) => throwIfActionError(await updateGroupAvatar({ chatId, asset })),
    onSuccess: () => {
      toast.success("Group photo updated");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to update group photo",
      );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKeys.chatInfo(chatId) }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
        invalidateChatCollections(queryClient),
      ]);
    },
  });
}

export function useSetDisappearingTimer(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (durationSec: number | null) =>
      throwIfActionError(await setDisappearingTimer({ chatId, durationSec })),
    onSuccess: (_result, durationSec) => {
      toast.success(
        durationSec ? "Disappearing messages on" : "Disappearing messages off",
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update disappearing messages",
      );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKeys.chatInfo(chatId) }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.chatMessages(chatId),
        }),
      ]);
    },
  });
}

export function useMarkViewOnceOpened(chatId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: number) => {
      const result = await markViewOnceOpened({ messageId });
      if (result.error || !result.data) {
        throw new Error(result.error ?? "Failed to open media");
      }
      return result.data as { attachments: MessageAttachment[] };
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to open media",
      );
    },
    onSuccess: (_data, messageId) => {
      // Flip local state immediately; the receipt rides delta for others.
      queryClient.setQueryData<Message[]>(
        chatKeys.chatMessages(chatId),
        (old) =>
          (old ?? []).map((message) =>
            message.id === messageId
              ? { ...message, viewOnceState: "opened" }
              : message,
          ),
      );
    },
  });
}

export interface MessageReactor {
  userId: string;
  reaction: string;
  reactedAt: Date | null;
  userName: string | null;
  username: string | null;
  userImage: string | null;
}

export function useMessageReactors(messageId: number | null | undefined) {
  const realtimeStatus = useRealtimeStatus();
  return useQuery({
    queryKey: chatKeys.messageReactors(messageId ?? -1),
    queryFn: async () => {
      if (!messageId) return [] as MessageReactor[];
      const result = await getMessageReactors(messageId);
      if (result.error) {
        throw new Error(result.error);
      }
      return (result.data ?? []).map((row) => ({
        ...row,
        reactedAt: row.reactedAt ? coerceDate(row.reactedAt) : null,
      })) as MessageReactor[];
    },
    enabled: !!messageId,
    staleTime: 2_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () =>
      resolveAdaptivePollMs(5_000, 10_000, !!messageId, realtimeStatus),
    refetchIntervalInBackground: true,
  });
}

export function useHeartbeatChatPresence(enabled = true) {
  return useMutation({
    mutationKey: ["chat-presence-heartbeat"],
    mutationFn: async () => {
      if (!enabled) return { data: { ok: false } };
      const result = await heartbeatChatPresence();
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
  });
}

export function useChatSurfaceSync(input: {
  enabled?: boolean;
  queryKeys?: ReadonlyArray<readonly unknown[]>;
  touchPresence?: boolean;
}) {
  const queryClient = useQueryClient();
  const enabled = input.enabled ?? true;
  const touchPresence = input.touchPresence !== false;
  const heartbeatMutation = useHeartbeatChatPresence(enabled);
  const queryKeysRef = useRef<ReadonlyArray<readonly unknown[]>>(
    input.queryKeys ?? [],
  );

  useEffect(() => {
    queryKeysRef.current = input.queryKeys ?? [];
  }, [input.queryKeys]);

  useEffect(() => {
    if (!enabled) return;

    const refreshSurface = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }

      for (const queryKey of queryKeysRef.current) {
        void queryClient.invalidateQueries({ queryKey });
      }

      if (touchPresence) {
        heartbeatMutation.mutate();
      }
    };

    refreshSurface();
    const intervalId = window.setInterval(
      refreshSurface,
      PRESENCE_HEARTBEAT_MS,
    );
    window.addEventListener("focus", refreshSurface);
    document.addEventListener("visibilitychange", refreshSurface);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshSurface);
      document.removeEventListener("visibilitychange", refreshSurface);
    };
  }, [enabled, heartbeatMutation.mutate, queryClient, touchPresence]);
}

export function useRequestChatUploadTarget(chatId: number) {
  return useMutation({
    mutationFn: (payload: {
      slot: ChatUploadSlot;
      fileName: string;
      mimeType: string;
      bytes: number;
    }) => requestChatUploadTarget({ chatId, ...payload }),
  });
}
